/**
 * Unity Translation Keys File Renderer
 * Generates TranslationKey class and static TranslationKeys class with all translation data
 */

import type { StringsItem } from '@/lib/stores/stringsStore';
import { stripHtmlTags } from '../../core/formatting';

/**
 * Render translation keys file for Unity C# exports
 * Generates TranslationKey class and static TranslationKeys class with all translation data
 * 
 * @param stringsItems - Array of string items with translations
 * @param namespace - C# namespace (default: 'Grimoire.Data')
 * @param className - Class name for the static keys class (default: 'TranslationKeys')
 * @param gameLanguages - Array of language codes configured for the game (e.g., ['en', 'nl', 'fr'])
 */
export function renderTranslationKeysFile(
  stringsItems: StringsItem[],
  namespace: string = 'Grimoire.Data',
  className: string = 'TranslationKeys',
  gameLanguages?: string[]
): string {
  // If no languages provided, extract from stringsItems
  if (!gameLanguages || gameLanguages.length === 0) {
    const languagesSet = new Set<string>();
    languagesSet.add('en'); // Always include English as default
    
    stringsItems.forEach(item => {
      if (item.translations && Array.isArray(item.translations)) {
        item.translations.forEach(trans => {
          if (trans.language_code) {
            languagesSet.add(trans.language_code);
          }
        });
      }
    });
    
    gameLanguages = Array.from(languagesSet).sort();
  }
  
  console.log(`🌐 Rendering translation keys for languages:`, gameLanguages);
  console.log(`📊 Processing ${stringsItems.length} string items`);
  let output = '// Auto-generated Translation Keys\n';
  output += '// This file is AUTO-GENERATED and can be safely regenerated.\n';
  output += '// DO NOT EDIT THIS FILE DIRECTLY - your changes will be lost.\n\n';
  output += 'using System;\n';
  output += 'using System.Collections.Generic;\n\n';
  output += `namespace ${namespace}\n{\n`;

  // Generate TranslationKey class
  output += '\tpublic class TranslationKey\n\t{\n';
  output += '\t\t/// <summary>Current language code used for implicit string conversion</summary>\n';
  output += '\t\tpublic static string CurrentLanguage { get; set; } = "en";\n\n';
  output += '\t\tpublic string Id { get; }\n';
  output += '\t\tprivate readonly Dictionary<string, string> _translations;\n\n';
  output += '\t\tpublic TranslationKey(string id, Dictionary<string, string> translations)\n\t\t{\n';
  output += '\t\t\tId = id;\n';
  output += '\t\t\t_translations = translations ?? new Dictionary<string, string>();\n';
  output += '\t\t}\n\n';
  output += '\t\t/// <summary>Get translation for a specific language</summary>\n';
  output += '\t\tpublic string this[string languageCode] =>\n';
  output += '\t\t\t_translations.TryGetValue(languageCode, out var text) ? text : Id;\n\n';
  output += '\t\t/// <summary>Get translation for current language</summary>\n';
  output += '\t\tpublic string GetText() => this[CurrentLanguage];\n\n';
  output += '\t\t/// <summary>Implicit conversion to string using current language</summary>\n';
  output += '\t\tpublic static implicit operator string(TranslationKey key) => key?.GetText() ?? "";\n\n';
  output += '\t\t/// <summary>ToString returns translation for current language</summary>\n';
  output += '\t\tpublic override string ToString() => GetText();\n';
  output += '\t}\n\n';

  // Generate TranslationKeys static class
  output += `\tpublic static class ${className}\n\t{\n`;

  // Generate Empty TranslationKey for empty/unset translatable fields
  output += '\t\t/// <summary>Empty translation key for unset translatable fields</summary>\n';
  output += '\t\tpublic static readonly TranslationKey Empty = new(\n';
  output += '\t\t\t"",\n';
  output += '\t\t\tnew Dictionary<string, string>\n\t\t\t{\n';
  gameLanguages.forEach((languageCode, langIndex) => {
    output += `\t\t\t\t["${languageCode}"] = ""`;
    if (langIndex < gameLanguages.length - 1) {
      output += ',';
    }
    output += '\n';
  });
  output += '\t\t\t}\n';
  output += '\t\t);\n\n';

  // Build a set to track which keys to generate (including array element keys)
  const keysToGenerate = new Map<string, { source_text: string, translations: any[] }>();
  
  stringsItems.forEach((stringItem) => {
    const keyName = stringItem.abbrev || `key_${stringItem.id}`;
    const safeKeyName = keyName.replace(/[^a-zA-Z0-9_]/g, '_');
    
    // Check if source_text is a JSON array
    let isArray = false;
    let arrayValues: string[] = [];
    
    try {
      const parsed = JSON.parse(stringItem.source_text);
      if (Array.isArray(parsed)) {
        isArray = true;
        // Strip HTML from array values
        arrayValues = parsed.map(v => stripHtmlTags(String(v)));
      }
    } catch {
      // Not JSON or not an array, treat as single value
    }
    
    if (isArray && arrayValues.length > 0) {
      // Generate a key for each array element
      arrayValues.forEach((_, idx) => {
        const elementKeyName = `${safeKeyName}_${idx}`;
        
        // Build translations for this array element
        const elementTranslations = (stringItem.translations || []).map(trans => {
          let translatedArray: string[] = [];
          try {
            const parsed = JSON.parse(trans.translated_text);
            if (Array.isArray(parsed)) {
              // Strip HTML from translated array values
              translatedArray = parsed.map(v => stripHtmlTags(String(v)));
            }
          } catch {
            // Not a valid array
          }
          
          return {
            language_code: trans.language_code,
            translated_text: translatedArray[idx] || ''
          };
        });
        
        keysToGenerate.set(elementKeyName, {
          source_text: arrayValues[idx],
          translations: elementTranslations
        });
      });
    } else {
      // Single value key - strip HTML from source text
      keysToGenerate.set(safeKeyName, {
        source_text: stripHtmlTags(stringItem.source_text),
        translations: (stringItem.translations || []).map(trans => ({
          language_code: trans.language_code,
          // Strip HTML from translated text
          translated_text: stripHtmlTags(trans.translated_text)
        }))
      });
    }
  });

  // Generate all keys
  let keyIndex = 0;
  keysToGenerate.forEach((keyData, keyName) => {
    output += `\t\tpublic static readonly TranslationKey ${keyName} = new(\n`;
    output += `\t\t\t"${keyName}",\n`;
    output += '\t\t\tnew Dictionary<string, string>\n\t\t\t{\n';
    
    // Create a map of existing translations (already HTML-stripped from earlier)
    const translationsMap = new Map<string, string>();
    keyData.translations.forEach(trans => {
      if (trans.language_code && trans.translated_text) {
        translationsMap.set(trans.language_code, trans.translated_text);
      }
    });
    
    console.log(`  🔑 Key "${keyName}": has ${translationsMap.size} translations, source_text="${keyData.source_text?.substring(0, 30)}..."`);
    
    // Generate entries for all configured game languages
    gameLanguages.forEach((languageCode, langIndex) => {
      let translatedText = translationsMap.get(languageCode);
      
      // If no translation exists, use fallback
      if (!translatedText) {
        // For English (default source language), use source_text (already HTML-stripped)
        if (languageCode === 'en' && keyData.source_text) {
          translatedText = keyData.source_text;
        } else {
          translatedText = '[not translated yet]';
        }
      }
      
      // Text is already HTML-stripped from earlier processing
      const escapedText = String(translatedText)
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/\n/g, '\\n')
        .replace(/\r/g, '\\r')
        .replace(/\t/g, '\\t');
      
      output += `\t\t\t\t["${languageCode}"] = "${escapedText}"`;
      
      if (langIndex < gameLanguages.length - 1) {
        output += ',';
      }
      
      output += '\n';
    });
    
    output += '\t\t\t}\n';
    output += '\t\t);\n';
    
    if (keyIndex < keysToGenerate.size - 1) {
      output += '\n';
    }
    keyIndex++;
  });

  output += '\t}\n';
  output += '}\n';

  return output;
}
